import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Lock, CheckCircle, Circle } from "@phosphor-icons/react";
import { Progress } from "@/components/ui/progress";
import { Seo } from "@/components/Seo";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/fers";

const toDate = (s) => new Date(`${s}T00:00:00`);

function StepRow({ s, today }) {
  const open = s.unlock <= today;
  const Icon = s.done ? CheckCircle : open ? Circle : Lock;
  return (
    <li className="flex gap-3 py-3 border-b border-border/60 last:border-0" data-testid={`shared-step-${s.id}`}>
      <Icon size={18} weight={s.done ? "fill" : "regular"} className={s.done ? "text-emerald-600 mt-0.5" : open ? "text-amber-600 mt-0.5" : "text-muted-foreground mt-0.5"} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${s.done ? "line-through text-muted-foreground" : "font-medium"}`}>{s.title}</p>
        <p className="text-[11px] font-mono-num text-muted-foreground">{s.done ? "Done" : open ? "Open" : "Upcoming"} · {open ? "unlocked" : "unlocks"} {fmtDate(toDate(s.unlock))}</p>
      </div>
    </li>
  );
}

export default function SharedChecklist() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/shared/checklist/${token}`).then((r) => setData(r.data)).catch((e) => setErr(errMsg(e))); }, [token]);
  if (err) return <div className="glass p-8 max-w-xl" data-testid="shared-error"><p className="font-display text-xl font-semibold">Link not available</p><p className="text-sm text-muted-foreground mt-2">{err}</p></div>;
  if (!data) return <p className="text-sm text-muted-foreground">Loading...</p>;
  const done = data.steps.filter((s) => s.done).length;
  return (
    <div className="max-w-4xl fade-up" data-testid="shared-checklist">
      <Seo title="Shared retirement checklist" path="/shared" noindex />
      <p className="text-xs uppercase tracking-widest text-cyan-700 dark:text-cyan-400">Read-only shared view</p>
      <h1 className="text-4xl sm:text-5xl font-semibold text-grad-head mt-2" data-testid="shared-title">{data.first_name}'s retirement checklist</h1>
      {data.retirement_day && <p className="text-sm text-muted-foreground mt-3" data-testid="shared-retirement-day">Retirement day: {fmtDate(toDate(data.retirement_day))}</p>}
      <div className="grid gap-6 lg:grid-cols-[1fr_280px] mt-8">
        <div className="glass p-6"><p className="font-display font-semibold mb-2">Steps</p><ol data-testid="shared-steps">{data.steps.map((s) => <StepRow key={s.id} s={s} today={data.today} />)}</ol></div>
        <div className="glass p-6 h-fit">
          <p className="font-display font-semibold">Progress</p>
          <p className="font-mono-num text-3xl font-semibold text-grad-money mt-2" data-testid="shared-progress-count">{done}/{data.steps.length}</p>
          <Progress value={data.steps.length ? (done / data.steps.length) * 100 : 0} className="mt-3" />
          <p className="text-xs text-muted-foreground mt-4">This page updates as {data.first_name} checks off steps. You can't make changes here.</p>
        </div>
      </div>
      {data.questions.length > 0 && (
        <div className="glass p-6 mt-6" data-testid="shared-hr-questions">
          <p className="font-display font-semibold mb-3">Questions for HR</p>
          <ol className="space-y-4">
            {data.questions.map((q, i) => (
              <li key={q.id} className="flex gap-3 text-sm" data-testid={`shared-hr-question-${q.id}`}>
                <span className="font-mono-num text-muted-foreground w-5 shrink-0">{i + 1}.</span>
                <div><span className="text-[10px] uppercase tracking-wider text-cyan-700 dark:text-cyan-400">{q.topic}</span><p>{q.question}</p>
                  <p className={`mt-1 text-sm ${data.answers[q.id] ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground italic"}`} data-testid={`shared-hr-answer-${q.id}`}>{data.answers[q.id] ? `HR answer: ${data.answers[q.id]}` : "No answer yet"}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
